import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, Stack, Paper, Chip } from '@mui/material';
import { Search, BarChart3, Bell, Calculator } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Phase 0 placeholder for the dealer/enthusiast cockpit. Replaced in Phase 1
// by Deal Dashboard + Watchlists + Margin Calculator. Marketing copy aimed at
// dealers/flippers; soft "browse manually" link sits below the fold.

const Surface = ({ icon: Icon, title, body }) => (
  <Paper sx={{ p: 3, borderRadius: 2 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
      <Icon size={20} />
      <Typography variant="h6">{title}</Typography>
      <Chip label="Coming soon" size="small" sx={{ ml: 'auto' }} />
    </Stack>
    <Typography variant="body2" color="text.secondary">{body}</Typography>
  </Paper>
);

export default function CockpitShell() {
  const { userData } = useAuth();
  const role = userData?.role || 'dealer';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 4, sm: 6 } }}>
      <Stack spacing={1.5} mb={5}>
        <Typography variant="overline" color="secondary">
          {role === 'enthusiast' ? 'Enthusiast cockpit' : 'Dealer cockpit'}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.025em' }}>
          Find the deals before they hit your lot.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
          BlueShift Auto scans the national market for vehicles you can buy below local
          retail and flip — ranked by projected margin, with watchlist alerts the
          moment something hits.
        </Typography>
      </Stack>

      <Stack spacing={2} mb={6}>
        <Surface
          icon={BarChart3}
          title="Deal Dashboard"
          body="Today's top vehicles ranked by projected margin across your watchlists and your local market."
        />
        <Surface
          icon={Bell}
          title="Watchlists & alerts"
          body="Saved searches that fire email/SMS the instant a matching listing appears. Built on the existing saved-search engine."
        />
        <Surface
          icon={Calculator}
          title="Margin calculator"
          body="Buy + transport + recon + fees → projected retail → net margin, on every vehicle detail page."
        />
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Search size={16} />
        <Typography variant="body2" color="text.secondary">
          Need to look up a specific vehicle?
        </Typography>
        <Button component={RouterLink} to="/browse" variant="text" size="small">
          Browse manually
        </Button>
      </Stack>
    </Box>
  );
}
